import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Banner from '../../assets/about-us-banner.png';
import Banner2 from '../../assets/about-us-banner-2.png';

const BASE_API = (process.env.REACT_APP_BASE_API || '').replace(/\/+$/, '')
const UPLOAD_PATH = 'uploads' // same folder your product images use

const toImageUrl = (src) => {
    if (!src) return null
    if (/^(https?:)?\/\//.test(src) || src.startsWith('data:')) return src
    const clean = String(src).replace(/^\/+/, '')
    return `${BASE_API}/${clean.includes('/') ? clean : `${UPLOAD_PATH}/${clean}`}`
}

const DEFAULTS = {
    heroTitle: 'Our Story',
    heroSubtitle: 'Rooted in Ayurveda. Inspired by you.',
    missionTitle: 'Our Mission',
    missionText:
        'At Jeetayu, we believe in the power of nature to create a healthier, happier life. Our mission is to bring authentic Ayurvedic products, traditional clothing and elegant jewellery to your doorstep, combining timeless wisdom with modern convenience.',
    mission: [
        { title: 'Natural & Pure', icon: 'leaf' },
        { title: 'Trusted Quality', icon: 'shield' },
        { title: 'Customer First', icon: 'people' },
    ],
    whyTitle: 'Why Choose Us',
    why: [
        { title: 'Authentic Ayurveda', icon: 'herb' },
        { title: 'Curated Fashion', icon: 'shirt' },
        { title: 'Elegant Jewellery', icon: 'jewel' },
        { title: 'Secure Shopping', icon: 'lock' },
    ],
    bottomTitle: 'Wellness. Style. You.',
    bottomText: 'Join our journey to a better tomorrow.',
    buttonText: 'Start Shopping',
    buttonLink: '/shop',
}

/* ---------- Decorative leaf ---------- */
const Leaf = ({ className = '', style }) => (
    <svg viewBox="0 0 100 160" className={className} style={style} aria-hidden="true">
        <path d="M50 4C14 40 8 100 50 156 92 100 86 40 50 4z" fill="currentColor" />
        <path d="M50 20v132" stroke="#fff" strokeOpacity=".5" strokeWidth="2" />
        <path d="M50 50l-18 18M50 78l-22 22M50 50l18 18M50 78l22 22" stroke="#fff" strokeOpacity=".4" strokeWidth="1.5" fill="none" />
    </svg>
);

/* ---------- Icons (the admin picks one by name) ---------- */
const Ico = ({ children }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-9 w-9 sm:h-11 sm:w-11" aria-hidden="true">
        {children}
    </svg>
);

const ICONS = {
    leaf: <Ico><path d="M12 21c-5-3-8-7-8-11 3 0 6 1 8 4 2-3 5-4 8-4 0 4-3 8-8 11z" /><path d="M12 14V8c0-2 1-4 3-5" /></Ico>,
    shield: <Ico><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M8.5 12l2.5 2.5 4.5-5" /></Ico>,
    people: <Ico><circle cx="8" cy="8" r="2.5" /><circle cx="16" cy="8" r="2.5" /><path d="M3 19c0-3 2-5 5-5s5 2 5 5M11 19c0-3 2-5 5-5s5 2 5 5" /></Ico>,
    herb: <Ico><path d="M12 21c-5-3-8-7-8-11 3 0 6 1 8 4 2-3 5-4 8-4 0 4-3 8-8 11z" /><circle cx="12" cy="6" r="1.5" /></Ico>,
    shirt: <Ico><path d="M12 6a2 2 0 10-2-2" /><path d="M12 6v2L3 15c-1 .8-.4 2 .7 2h16.6c1.1 0 1.7-1.2.7-2l-9-7" /></Ico>,
    jewel: <Ico><path d="M7 4h10l3 5-8 11L4 9z" /><path d="M4 9h16M9 4l3 5 3-5M12 9v11" /></Ico>,
    lock: <Ico><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /><circle cx="12" cy="15.5" r="1" /></Ico>,
};

/* ---------- Reusable card ---------- */
const FeatureCard = ({ title, icon }) => (
    <li className="flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/90 px-3 py-6 text-center shadow-[0_4px_20px_rgba(47,107,31,0.08)] ring-1 ring-[#e3ead8] backdrop-blur transition hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(47,107,31,0.15)] sm:py-8">
        <span className="text-[#2f6b1f]">{ICONS[icon] || ICONS.leaf}</span>
        <span className="text-xs font-medium leading-tight text-[#2f4a26] sm:text-sm md:text-base">{title}</span>
    </li>
);

const fadeLeft = {
    WebkitMaskImage:
        'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 12%, rgba(0,0,0,0.5) 28%, rgba(0,0,0,0.85) 45%, #000 60%)',
    maskImage:
        'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 12%, rgba(0,0,0,0.5) 28%, rgba(0,0,0,0.85) 45%, #000 60%)',
};

const About = () => {
    const [c, setC] = useState(DEFAULTS)
    const [heroImg, setHeroImg] = useState(Banner)
    const [bottomImg, setBottomImg] = useState(Banner2)

    useEffect(() => {
        const controller = new AbortController()
        fetch(`${BASE_API}/about`, { signal: controller.signal })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((d) => {
                setC({ ...DEFAULTS, ...d })
                if (d.hero_img) setHeroImg(toImageUrl(d.hero_img))
                if (d.bottom_img) setBottomImg(toImageUrl(d.bottom_img))
            })
            .catch(() => {}) // keep the defaults
        return () => controller.abort()
    }, [])

    return (
        <div className="overflow-x-hidden bg-[#f6f7f2] pt-16 text-gray-800 sm:pt-20">
            {/* ---------- Hero ---------- */}
            <section className="relative isolate overflow-hidden" style={{ background: 'linear-gradient(100deg, #1d4a14 0%, #2f6b1f 55%, #3d7a2a 100%)' }}>
                <img
                    src={heroImg}
                    alt={c.heroTitle}
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = Banner }}
                    style={fadeLeft}
                    className="absolute inset-y-0 right-0 h-full w-full object-cover object-right sm:w-[85%] md:w-[70%] lg:w-[62%]"
                />
                <div className="relative mx-auto flex min-h-[170px] max-w-6xl flex-col justify-center px-5 py-10 sm:min-h-[220px] sm:px-8 md:min-h-[280px] lg:min-h-[320px]">
                    <h1 className="font-serif text-3xl font-semibold text-white drop-shadow sm:text-5xl lg:text-6xl">{c.heroTitle}</h1>
                    <p className="mt-2 max-w-[18rem] text-sm font-medium text-white/90 drop-shadow sm:mt-3 sm:max-w-md sm:text-lg">{c.heroSubtitle}</p>
                </div>
            </section>

            {/* ---------- Mission + Why Choose Us ---------- */}
            <div className="relative">
                <Leaf className="pointer-events-none absolute -left-6 top-24 h-28 rotate-[25deg] text-[#cfe0b8] sm:-left-4 sm:h-40" />
                <Leaf className="pointer-events-none absolute -right-6 top-6 h-24 -rotate-[25deg] text-[#cfe0b8] sm:h-36" />
                <Leaf className="pointer-events-none absolute -right-8 bottom-10 h-28 rotate-[20deg] text-[#cfe0b8] sm:h-44" />

                <section className="relative mx-auto max-w-6xl px-5 pb-6 pt-10 sm:px-8 sm:pt-14">
                    <h2 className="font-serif text-2xl font-semibold text-[#2f4a26] sm:text-3xl">{c.missionTitle}</h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-600 sm:mt-4 sm:text-base md:text-lg">{c.missionText}</p>
                    <ul className="mt-8 grid grid-cols-3 gap-3 sm:gap-6">
                        {c.mission.map((m, i) => <FeatureCard key={i} {...m} />)}
                    </ul>
                </section>

                <section className="relative mx-auto max-w-6xl px-5 pb-12 pt-6 sm:px-8 sm:pb-16">
                    <h2 className="font-serif text-2xl font-semibold text-[#2f4a26] sm:text-3xl">{c.whyTitle}</h2>
                    <ul className="mt-6 grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-4">
                        {c.why.map((w, i) => <FeatureCard key={i} {...w} />)}
                    </ul>
                </section>
            </div>

            {/* ---------- Bottom banner ---------- */}
            <section className="relative isolate overflow-hidden" style={{ background: 'linear-gradient(100deg, #1d4a14 0%, #2f6b1f 60%, #3d7a2a 100%)' }}>
                <img
                    src={bottomImg}
                    alt={c.bottomTitle}
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = Banner2 }}
                    style={fadeLeft}
                    className="absolute inset-y-0 right-0 h-full w-full object-cover object-right sm:w-[85%] md:w-[70%] lg:w-[62%]"
                />
                <div className="relative mx-auto flex min-h-[130px] max-w-6xl flex-col justify-center gap-4 px-5 py-8 sm:min-h-[170px] sm:flex-row sm:items-center sm:justify-between sm:px-8">
                    <div>
                        <h3 className="font-serif text-2xl font-semibold text-white drop-shadow sm:text-3xl">{c.bottomTitle}</h3>
                        <p className="mt-1 text-sm text-white/85 sm:text-base">{c.bottomText}</p>
                    </div>
                    <Link to={c.buttonLink || '/shop'} className="w-fit rounded-md bg-white px-6 py-2.5 text-sm font-semibold text-[#2f6b1f] shadow transition hover:bg-[#f3f4ef]">
                        {c.buttonText}
                    </Link>
                </div>
            </section>
        </div>
    )
}

export default About;